import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MapPin, Bed, ShieldCheck } from "lucide-react";
import { BrandMark } from "@/components/AppShell";
import TrustBadge from "@/components/TrustBadge";
import TrustRadar from "@/components/TrustRadar";
import HcahpsPanel from "@/components/HcahpsPanel";
import HcahpsStars from "@/components/HcahpsStars";
import HospitalAiSummary from "@/components/ai/HospitalAiSummary";
import api from "@/lib/api";

export default function HospitalPublicProfile() {
  const { id } = useParams();
  const [hospital, setHospital] = useState(null);
  const [trust, setTrust] = useState(null);

  useEffect(() => {
    (async () => {
      const [h, t] = await Promise.all([
        api.get(`/hospitals/${id}`),
        api.get(`/trust-index/hospital/${id}`),
      ]);
      setHospital(h.data);
      setTrust(t.data);
    })();
  }, [id]);

  if (!hospital) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground text-sm">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/"><BrandMark /></Link>
          <Link to="/directory" className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1">
            <ArrowLeft size={14} /> Back to directory
          </Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-lg border border-border bg-white p-6 lg:p-8">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="overline">Verified · {hospital.type}</div>
                <h1 className="mt-2 font-serif text-4xl text-primary" data-testid="hospital-name">{hospital.name}</h1>
                <p className="text-muted-foreground mt-1">{hospital.accreditation}</p>
                {hospital.hcahps_stars ? (
                  <div className="mt-3 flex items-center gap-2">
                    <HcahpsStars stars={hospital.hcahps_stars} testid="hospital-header-hcahps" />
                    <span className="text-xs text-muted-foreground">
                      HCAHPS Patient Experience · <span className="text-primary font-semibold">{hospital.hcahps_score}</span>/100
                    </span>
                  </div>
                ) : null}
              </div>
              <TrustBadge tier={hospital.tier} score={hospital.trust_score} />
            </div>
            <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <InfoStat icon={MapPin} label="City" value={hospital.city} />
              <InfoStat icon={Bed} label="Beds" value={hospital.beds} />
              <InfoStat icon={ShieldCheck} label="Accreditation" value={hospital.accreditation} />
            </div>
          </div>

          <div className="rounded-lg border border-border bg-white p-6">
            <div className="overline mb-3">Trust Index breakdown</div>
            {trust && <TrustRadar criteria={trust.criteria} />}
          </div>

          <HospitalAiSummary hospitalId={id} />

          <HcahpsPanel hospitalId={id} />
        </div>

        <div className="space-y-6">
          {trust && (
            <div className="rounded-lg border border-border bg-white p-6">
              <div className="overline mb-3">Priority improvements</div>
              <ul className="space-y-3">
                {trust.roadmap.map((r) => (
                  <li key={r.area} className="text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-primary">{r.area}</span>
                      <span className="text-xs text-muted-foreground">{r.current} → {r.target}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{r.recommendation}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoStat({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground">
        <Icon size={12} /> {label}
      </div>
      <div className="mt-1 text-primary font-medium">{value}</div>
    </div>
  );
}
